import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { runCustomerImport, type ImportOutcome, type ImportResult } from "../../api/customerImport";
import { Link as RouterLink } from "react-router-dom";
import { fileHeadings, guessColumns, IMPORT_COLUMNS, IMPORT_TEMPLATE, readImportFile, type ColumnMap, type ImportColumn, type ImportRow } from "../../domain/customerImportCsv";
import { guessPlanColumns, PLAN_DETAIL_COLUMNS, plansFileName, splitPlans, type PlanColumnMap, type PlanSplit } from "../../domain/migrationSplit";
import { PROPOSE_PATH } from "../../components/nav";
import { MigratePlans } from "./MigratePlans";

const FIELD_LABEL: Record<ImportColumn, string> = {
  username: "Username (their sign-in today)", mobile: "Mobile (recommended)", name: "Name (required)", gender: "Gender", plan: "Plan (required)",
  expiry: "Expiry (required)", password: "Password", deviceid: "Device ID", logins_allowed: "Logins allowed",
  address: "Address", city: "City", state: "State", pin: "PIN",
};

const PLAN_LABEL: Record<string, string> = {
  price: "Plan price", validity_days: "Plan days", speed_mbps: "Speed (Mbps)", product: "Product", devices: "Logins on the plan",
  daily_minutes: "Daily minutes", prices_include_gst: "Price includes GST",
};

/** Bring existing customers across with the expiry they paid for: no charge, invoice, receipt or SMS. */
export function ImportCustomers() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [text, setText] = useState("");
  const [headings, setHeadings] = useState<string[]>([]);
  const [columns, setColumns] = useState<ColumnMap>({});
  const [planColumns, setPlanColumns] = useState<PlanColumnMap>({});
  const [fileName, setFileName] = useState("");
  const [problem, setProblem] = useState("");
  const [split, setSplit] = useState<(PlanSplit & { fileName: string }) | null>(null);
  const [trial, setTrial] = useState<ImportOutcome | null>(null);
  const [done, setDone] = useState<ImportOutcome | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const run = async (dry: boolean) => {
    setError("");
    setBusy(dry ? "Checking" : "Importing");
    try {
      const r = await runCustomerImport(identity, ns, rows, dry, (n) => setBusy(`${dry ? "Checking" : "Importing"} ${n} of ${rows.length}`));
      if (dry) setTrial(r);
      else setDone(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not finish. Upload the file again: customers already imported are refused, not repeated.");
    } finally {
      setBusy("");
    }
  };

  const pick = async (file?: File) => {
    setTrial(null);
    setDone(null);
    setRows([]);
    setSplit(null);
    if (!file) return;
    const text = await file.text();
    const found = fileHeadings(text);
    setText(text);
    setHeadings(found);
    setFileName(file.name);
    const guessedPlan = guessPlanColumns(found);
    setPlanColumns(guessedPlan);
    applyColumns(text, guessColumns(found), guessedPlan, file.name);
  };

  const applyColumns = (from: string, map: ColumnMap, plan: PlanColumnMap, name: string) => {
    setColumns(map);
    setPlanColumns(plan);
    setTrial(null);
    const s = splitPlans(from, map.plan, plan);
    setSplit(s.consolidated ? { ...s, fileName: plansFileName(name) } : null);
    const read = readImportFile(from, map);
    setProblem(read.problem ?? "");
    setRows(read.rows);
  };

  const attentionCsv = (list: ImportResult[]) => {
    const byLine = new Map(rows.map((r) => [r.line, r]));
    const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
    return ["line,username,mobile,name,why", ...list.map((r) => {
      const row = byLine.get(r.line);
      return [String(r.line), row?.username ?? "", row?.mobile ?? "", row?.name ?? "", r.reason ?? ""].map(cell).join(",");
    })].join("\n") + "\n";
  };

  const shown = done ?? trial;
  const notGood = shown?.results.filter((r) => r.status === "refused" || r.status === "failed") ?? [];
  const lines = (pick: (r: ImportResult) => boolean | undefined) => (shown?.results ?? []).filter(pick).map((r) => r.line);
  const noMobile = lines((r) => r.mobile_missing);
  const shared = lines((r) => r.mobile_shared);
  const noPassword = lines((r) => r.needs_password);
  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h1">Import customers</Typography>
      <Alert severity="info">
        Bring your existing customers across with the expiry they already paid for, as many as you like or a few at a time. There is no charge, invoice, receipt or SMS.
        Create the plans first: each row names a plan by its exact name. PIN-backed plans cannot be imported. Opening balances and email addresses are not imported yet.
        Mobile is the recommended main detail. A customer with no username signs in with their mobile. Your file stays in your browser and is not kept.
      </Alert>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Button href={`data:text/csv;charset=utf-8,${encodeURIComponent(IMPORT_TEMPLATE)}`} download="customers-template.csv">Download template</Button>
          <Button component="label" variant="outlined">
            Choose file
            <input hidden type="file" accept=".csv,text/csv" onChange={(e) => void pick(e.target.files?.[0])} />
          </Button>
          {rows.length > 0 && <Typography variant="body2">{rows.length} customers in the file</Typography>}
          <Button disabled={!rows.length || Boolean(busy)} onClick={() => void run(true)}>Trial run</Button>
          <Button variant="contained" disabled={!trial?.good || Boolean(busy) || Boolean(done)} onClick={() => void run(false)}>
            Import {trial?.good ?? 0} customers
          </Button>
          {busy && <Typography variant="body2">{busy}</Typography>}
        </Stack>
      </Paper>
      {headings.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Match your columns</Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            {IMPORT_COLUMNS.map((c) => (
              <TextField key={c} select size="small" sx={{ minWidth: 220 }} label={FIELD_LABEL[c]} value={columns[c] ?? ""}
                onChange={(e) => {
                  const next = { ...columns };
                  if (e.target.value) next[c] = e.target.value;
                  else delete next[c];
                  applyColumns(text, next, planColumns, fileName);
                }}>
                <MenuItem value="">Not in my file</MenuItem>
                {headings.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </TextField>
            ))}
          </Stack>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Plan details, if your file has them. We then compare your plans with ours before any customer goes in.</Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            {PLAN_DETAIL_COLUMNS.map((c) => (
              <TextField key={c} select size="small" sx={{ minWidth: 220 }} label={PLAN_LABEL[c] ?? c} value={planColumns[c] ?? ""}
                onChange={(e) => {
                  const next = { ...planColumns };
                  if (e.target.value) next[c] = e.target.value;
                  else delete next[c];
                  applyColumns(text, columns, next, fileName);
                }}>
                <MenuItem value="">Not in my file</MenuItem>
                {headings.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </TextField>
            ))}
          </Stack>
        </Paper>
      )}
      {split?.problem && <Alert severity="warning">{split.problem}</Alert>}
      {split && !split.problem && split.rows.length > 0 && <MigratePlans rows={split.rows} />}
      {split && !split.problem && (
        <Alert severity="info" data-testid="plans-split">
          This file holds plans too: {split.planCount} found. Only the plan columns go in the plans file, never a customer&apos;s details.
          Match them below. To have AI read a messy file instead, download the plans file for Propose from a document.
          {split.conflicts.length > 0 && ` Check these names, which have two different rows: ${split.conflicts.join(", ")}.`}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" href={`data:text/csv;charset=utf-8,${encodeURIComponent(split.plansCsv)}`} download={split.fileName}>Download plans file</Button>
            <Button size="small" component={RouterLink} to={PROPOSE_PATH}>Propose from a document</Button>
          </Stack>
        </Alert>
      )}
      {problem && <Alert severity="error">{problem}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {shown?.stopped && (
        <Alert severity="error">
          Stopped: {shown.stopped.reason} {shown.good} customers were handled before it stopped. Upload the same file again: customers already imported are refused, not repeated.
        </Alert>
      )}
      {trial && !done && (
        <Alert severity="info">
          Trial run saved nothing. {trial.good} of {rows.length} can be imported. Active subscribers today: {trial.activeBefore}, after import: {trial.activeBefore + trial.good}.
          Your AnkhOS plan follows the month's average of active subscribers, so a mid-month import moves the bill by its share of the month.
        </Alert>
      )}
      {done && <Alert severity="success">Imported {done.good} of {rows.length} customers.</Alert>}
      {noMobile.length > 0 && (
        <Alert severity="warning">
          Mobile missing on lines {noMobile.join(", ")}. These customers are imported and can go online, but cannot use the customer portal until a mobile is added and verified.
        </Alert>
      )}
      {shared.length > 0 && <Alert severity="warning">Same mobile as another customer on lines {shared.join(", ")}. Each stays a separate customer. Please check these.</Alert>}
      {noPassword.length > 0 && <Alert severity="warning">No password on lines {noPassword.join(", ")}. These customers need a new password: use Send login details.</Alert>}
      {!split && notGood.some((r) => r.reason?.startsWith("no plan named")) && (
        <Alert severity="warning">
          Some plan names are not on your plans list. Add each plan&apos;s price, days and speed to your file and choose those columns above to compare and create them here, or use <RouterLink to={PROPOSE_PATH}>Propose from a document</RouterLink>.
        </Alert>
      )}
      {notGood.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1">{notGood.length} rows need attention</Typography>
            <Button size="small" href={`data:text/csv;charset=utf-8,${encodeURIComponent(attentionCsv(notGood))}`} download="customers-to-fix.csv">Download this list</Button>
          </Stack>
          <Table size="small">
            <TableHead><TableRow><TableCell>Line</TableCell><TableCell>Why</TableCell></TableRow></TableHead>
            <TableBody>
              {notGood.slice(0, 500).map((r) => <TableRow key={r.line}><TableCell>{r.line}</TableCell><TableCell>{r.reason}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
